import { FormEvent, useEffect, useState } from 'react';
import { useToggle } from 'react-use';
import { Common } from '@thecvlb/design-system';

import { selectOrchestrate } from 'store';

import { AltSignupStepProps } from 'containers/SignUp/Content/content.types';
import WhereToCheckBloodPressure from 'modals/WhereToCheckBloodPressure';

import { useAppSelector } from 'hooks';
import useSubmitOrchestrateForm from 'hooks/useSubmitOrchestrateForm';

import Heading from '../../components/Heading';

const BloodPressure: React.FC<AltSignupStepProps> = ({ onContinue }) => {
  const { mif_qa: mifQa } = useAppSelector(selectOrchestrate);
  const [showModal, toggleModal] = useToggle(false);
  const [data, setData] = useState({ dia: 0, sys: 0 });
  const { send, isLoading } = useSubmitOrchestrateForm();

  const errors = {
    dia:
      !!data.dia && data.dia < 40
        ? { message: 'Please enter a valid diastolic blood pressure.' }
        : undefined,
    sys:
      !!data.sys && data.sys < 80
        ? { message: 'Please enter a valid systolic blood pressure.' }
        : undefined
  };

  const handleSubmitForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (errors.sys || errors.dia) return;
    send(
      'mif_qa',
      [{ answer: JSON.stringify(data), question: 'current_blood_pressure' }],
      onContinue
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const onInit = () => {
    try {
      const bloodPressureAnswer = mifQa.find((q) => q.question === 'current_blood_pressure');
      if (bloodPressureAnswer) {
        const { sys, dia } = JSON.parse(String(bloodPressureAnswer.answer));
        setData({ dia: Number(dia), sys: Number(sys) });
      }
    } catch {}
  };

  useEffect(onInit, []);

  return (
    <div className="flex flex-col gap-6 max-md:h-full">
      <WhereToCheckBloodPressure isOpen={showModal} onClose={() => toggleModal(false)} />
      <Heading
        category="Plan"
        subtitle="Understanding your blood pressure helps us tailor the Triple Therapy Program to your unique health needs, ensuring you receive the best possible care."
        title="What is your current blood pressure?"
      />
      <form className="flex grow flex-col gap-4" onSubmit={handleSubmitForm}>
        <div className="grid grid-cols-2 gap-6">
          <Common.Input
            errors={errors.sys}
            helper={errors.sys?.message}
            label="SYS"
            max={300}
            min={0}
            name="sys"
            size="lg"
            type="number"
            value={data.sys || ''}
            required
            onChange={handleChange}
          />
          <Common.Input
            errors={errors.dia}
            helper={errors.dia?.message}
            label="DIA"
            max={200}
            min={0}
            name="dia"
            size="lg"
            type="number"
            value={data.dia || ''}
            required
            onChange={handleChange}
          />
        </div>
        <Common.Alert type="info" colorableBackground>
          <span className="text-base">
            If you don’t know your exact blood pressure, that's okay. Just provide an estimate of
            your average blood pressure.
          </span>
        </Common.Alert>
        <Common.Button
          color="blue"
          style="text-only"
          type="button"
          fullWidthOnMobile
          onClick={() => toggleModal(true)}
        >
          Where can I check my blood pressure?
        </Common.Button>
        <Common.Button
          className="mt-auto md:mx-auto"
          color="blue"
          isLoading={isLoading}
          type="submit"
          fullWidthOnMobile
        >
          Continue
        </Common.Button>
      </form>
    </div>
  );
};

export default BloodPressure;
